import { Component, ViewChild } from "@angular/core";
import {
  IonicPage,
  NavController,
  NavParams,
  PopoverController,
  ViewController,
} from "ionic-angular";
import moment from "moment";
import { Observable } from "rxjs/Observable";
import Meeting, { MeetingTeam } from "../../../models/idg/meeting";
import { CurantisDatetimePipe } from "../../../pipes/output-formatters/curantis-datetime";
import { IDGDataService } from "../../../providers/idg-data-service/idg-data-service";

/**
 * Generated class for the CreateVisitModalPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */

@IonicPage()
@Component({
  selector: "page-create-idg-meeting-modal",
  templateUrl: "create-idg-meeting-modal.html",
})
export class CreateIDGMeetingModalPage {
  @ViewChild("idgMeetingName") meetingNameInput;
  @ViewChild("idgPatient") patientInput;

  validForm: boolean = false;
  teamSet: boolean = true;
  patientSet: boolean = false;
  openIDGMeeting: boolean;
  teamsData = [];
  selectedTeam: MeetingTeam;
  patientData = [];
  patients: any[] = [];
  patientsFetching: boolean = false;
  showIdgTempChanges: boolean = false;
  meeting: Meeting;
  isEditing: boolean = false;
  headerTitle: string = "createIDGMeeting";
  curantisDatetime = new CurantisDatetimePipe();
  scheduledDate: string;
  scheduledTime: string;
  // Date picker lower bound and time picker default wheel, both in local time.
  minScheduledDate: { year: number; month: number; day: number };
  initialTimeValue: string;
  // Short-notice warning: meeting scheduled within the prep window of now.
  // Hardcoded 48h here; CO-2501 makes the window per-agency configurable.
  showShortNoticeWarning: boolean = false;
  private static readonly SHORT_NOTICE_HOURS = 48;

  constructor(
    public viewCtrl: ViewController,
    public navCtrl: NavController,
    public navParams: NavParams,
    public idgSvc: IDGDataService,
    public popover: PopoverController
  ) {
    this.teamsData = this.navParams.data.teamsData.teams;
    this.showIdgTempChanges = this.navParams.data.showIdgTempChanges;
    this.meeting = this.navParams.data.meeting;
    this.teamsData.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
    if (this.navParams.data.headerTitle) {
      this.headerTitle = this.navParams.data.headerTitle;
    }
    if (this.navParams.data.isEditing) {
      this.isEditing = this.navParams.data.isEditing;
    }
    const now = moment();
    this.initialTimeValue = now.format("HH:mm");
    // Create: today onward only (no past dates). Edit: any date selectable.
    if (!this.isEditing) {
      this.minScheduledDate = {
        year: now.year(),
        month: now.month() + 1, // moment months are 0-indexed
        day: now.date(),
      };
    }
    if (this.meeting && this.meeting.scheduledDatetime) {
      // Backend stores UTC; show local in the pickers.
      const scheduledLocal = moment
        .utc(String(this.meeting.scheduledDatetime), [
          "YYYY/MM/DD HH:mm:ss",
          moment.ISO_8601,
        ])
        .local();
      if (scheduledLocal.isValid()) {
        this.scheduledDate = scheduledLocal.format("YYYY-MM-DD");
        this.scheduledTime = scheduledLocal.format("HH:mm");
      }
    }
  }

  // Parse scheduledDate (a "YYYY-MM-DD" string or a JS Date string) as a local calendar date;
  // new Date("YYYY-MM-DD") parses as UTC and can shift the day in negative-offset zones.
  private getScheduledDatePart(): string {
    const isoDate = moment(this.scheduledDate, "YYYY-MM-DD", true);
    const parsed = isoDate.isValid()
      ? isoDate
      : moment(new Date(this.scheduledDate));
    return parsed.format("YYYY-MM-DD");
  }

  private getScheduledDatetimeUtc(): string {
    const datePart = this.getScheduledDatePart();
    return moment(`${datePart} ${this.scheduledTime}`, "YYYY-MM-DD HH:mm")
      .utc()
      .format("YYYY/MM/DD HH:mm:ss");
  }

  teamChanged() {
    this.teamSet = false;
    this.patientData = [];
    this.patients = [];
    this.patientsFetching = true;
    this.getPatientsForTeamData().subscribe(
      (data) => {
        this.patientData = data["content"];

        this.patients = [...this.patientData];
        this.patientsFetching = false;
        this.isFormValid();
      },
      (err) => {
        this.patientsFetching = false;
      }
    );
  }

  patientChanged(event: any) {
    this.patients = [...event];
    this.isFormValid();
  }

  meetingNameChanged() {
    this.isFormValid();
  }

  openTeamOptions(ev) {
    let options = [];
    options = this.teamsData.map((t) => {
      return {
        name: t.name,
        value: t.id,
        checked: this.selectedTeam && this.selectedTeam.id === t.id,
      };
    });

    const noSort = true;
    const searchable = false;
    const teamPopover = this.popover.create(
      "CareTeamsSelectPage",
      {
        options,
        searchable,
        noSort,
        isMultiSelect: false,
        isSelectAll: false,
        isAutoWidth: true,
        containerWidth: 519,
        hasOptionDescription: false,
      },
      { cssClass: "multiple-select-custom-popover-for-idg" }
    );
    teamPopover.onDidDismiss((data) => {
      if (data) {
        this.selectedTeam = this.teamsData.find((t) => t.id == data);
        if (this.selectedTeam) {
          this.teamChanged();
        }
      }
    });
    teamPopover.present({ ev });
  }

  openPatientOptions(ev) {
    let options = [];
    options = this.patientData.map((p) => {
      let lastIdgDatetime = p.lastIdgDatetime
        ? this.curantisDatetime.transform(
            p.lastIdgDatetime,
            "local-date-with-days-since"
          )
        : null;
      return {
        name: `${p.lastName}, ${p.firstName} - ${p.qbosId} - ${p.patientChart?.patientStatus}`,
        description: "Last IDG Date: " + (lastIdgDatetime || "---"),
        value: p.id,
        lastIdgDatetime: p.lastIdgDatetime,
        checked: this.patients.some((e) => e.id == p.id),
      };
    });

    const noSort = true;
    const searchable = false;
    const patientPopover = this.popover.create(
      "CareTeamsSelectPage",
      {
        options,
        searchable,
        noSort,
        isMultiSelect: true,
        isSelectAll: true,
        isAutoWidth: true,
        containerWidth: 519,
        hasOptionDescription: true,
      },
      { cssClass: "multiple-select-custom-popover-for-idg" }
    );
    patientPopover.onDidDismiss((data) => {
      if (data && data.options) {
        this.patients = this.patientData.filter((p) =>
          data.options.find((o) => o.value === p.id && o.checked)
        );
      }
      this.isFormValid();
    });
    patientPopover.present({ ev });
  }

  isFormValid() {
    this.updateShortNoticeWarning();
    if (!this.meetingNameInput) {
      // cur-date-picker can emit during init, before the ViewChild resolves
      this.validForm = false;
      return this.validForm;
    }
    const scheduledSet = !!this.scheduledDate && !!this.scheduledTime;
    if (this.isEditing) {
      this.validForm = this.meetingNameInput.value.length > 0 && scheduledSet;
    } else {
      this.validForm =
        !!this.selectedTeam &&
        this.meetingNameInput.value.length > 0 &&
        this.patients.length > 0 &&
        scheduledSet;
    }
    return this.validForm;
  }

  private updateShortNoticeWarning() {
    if (!this.scheduledDate || !this.scheduledTime) {
      this.showShortNoticeWarning = false;
      return;
    }
    const datePart = this.getScheduledDatePart();
    const scheduled = moment(
      `${datePart} ${this.scheduledTime}`,
      "YYYY-MM-DD HH:mm"
    );
    this.showShortNoticeWarning =
      scheduled.isValid() &&
      scheduled.isBefore(
        moment().add(CreateIDGMeetingModalPage.SHORT_NOTICE_HOURS, "hours")
      );
  }

  onSubmit(event: any) {
    let data;
    if (this.isEditing) {
      data = {
        id: this.meeting.id,
        name: this.meetingNameInput.value,
      };
    } else {
      if (this.patients.length > 0) {
        data = {
          team: { id: this.selectedTeam.id },
          name: this.meetingNameInput.value,
        };
        if (this.showIdgTempChanges) {
          // if all patients selected, then meeting will be 'immediate=false' (see backend), triggers 'system hydration' when started
          const isEqualSize =
            this.patientData?.length === this.patients?.length;
          if (!isEqualSize) {
            data["patients"] = this.patients.map((p) => ({ id: p.id }));
          }
        }
      } else {
        data = {
          team: { id: this.selectedTeam.id },
          name: this.meetingNameInput.value,
        };
      }
    }
    data.scheduledDatetime = this.getScheduledDatetimeUtc();
    this.viewCtrl.dismiss(data);
  }

  onDismiss() {
    this.viewCtrl.dismiss();
  }

  getPatientsForTeamData(): Observable<any> {
    if (this.selectedTeam) {
      return new Observable((observer) => {
        try {
          this.idgSvc.getTeamsPatients(this.selectedTeam.id).subscribe(
            (data) => {
              observer.next(data);
              observer.complete();
            },
            (err) => {
              observer.error(err);
              observer.complete();
            }
          );
        } catch (error) {
          console.log("Error during data submission and building.", error);
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/inter-disciplinary-group/create-idg-meeting-modal/create-idg-meeting-modal.ts